/**
 * Configuration types and defaults for the forge-tryon widget.
 */

import type { AnalyticsCallback } from '@mirrably/core';
import type { CartPlatform } from './cart-bridge.js';

// Re-export so consumers can import AnalyticsCallback from the config module
// without needing to reach into the analytics sub-package directly.
export type { AnalyticsCallback };

/** Full configuration object for the ForgeTryon widget. */
export interface ForgeTryonConfig {
  /** Specific product ID to pre-select in the catalog. */
  productId?: string;
  /** Store/tenant identifier used to scope catalog requests. */
  storeId?: string;
  /**
   * Base URL of the Forge Tryon API.
   * @example "https://api.example.com/v1"
   */
  apiUrl: string;
  /**
   * API key sent as the X-API-Key request header on every fetch call.
   * Optional — omit for public/unauthenticated endpoints.
   */
  apiKey?: string;
  /** Visual theme preference. 'auto' follows the system prefers-color-scheme. */
  theme: 'light' | 'dark' | 'auto';
  /** BCP 47 locale tag used for UI strings. */
  locale: string;
  /**
   * GA4 measurement ID (e.g. "G-XXXXXXXXXX").
   *
   * When set, the widget automatically registers a GA4 adapter that forwards
   * every analytics event to the `gtag()` function on the page. The GA4
   * snippet must already be loaded by the host page.
   *
   * Corresponds to the `analytics` HTML attribute on the custom element.
   */
  analyticsId?: string;
  /**
   * Custom analytics callback — called for every tracked event.
   *
   * Use this to integrate with PostHog, Segment, Mixpanel, or any other
   * analytics service. The callback receives a strongly-typed `AnalyticsEvent`
   * from the forge-tryon analytics module.
   */
  onAnalytics?: AnalyticsCallback;
  /**
   * Enable debug mode — renders a wireframe of face landmarks over the camera
   * and hides the glasses model. Useful for verifying tracking accuracy.
   *
   * Corresponds to the `debug` HTML attribute on the custom element.
   */
  debug?: boolean;
  /**
   * Optional URL to POST an add-to-cart request when the user clicks the
   * Add to Cart button. When omitted, the widget still emits the 'add-to-cart'
   * event so the host page can handle it.
   *
   * Corresponds to the `cart-url` HTML attribute on the custom element.
   */
  cartUrl?: string;
  /**
   * Explicit e-commerce platform override for the cart bridge.
   * When omitted, the bridge auto-detects the platform from the host page DOM.
   *
   * Corresponds to the `cart-platform` HTML attribute on the custom element.
   * Accepted values: 'nopcommerce' | 'shopify' | 'woocommerce' | 'custom' | 'none'
   */
  cartPlatform?: CartPlatform;
  /**
   * URL of a test video to use instead of the live camera. When set, camera
   * access is skipped and the video plays in a loop. Useful for automated
   * testing and demos without a webcam.
   *
   * Corresponds to the `test-video-src` HTML attribute on the custom element.
   */
  testVideoSrc?: string;
  /**
   * Runtime multiplier applied on top of the GLB/default frame-width-to-IPD
   * ratio for operator/user fit calibration.
   *
   * Corresponds to the `ipd-scale-factor` HTML attribute.
   */
  ipdScaleFactor?: number;
  /**
   * Use the locally persisted PD measurement (`mirrably:calib_pdMm`) to derive
   * the runtime frame-width-to-IPD scale multiplier. Explicit
   * `ipd-scale-factor` takes precedence when both are present.
   *
   * Corresponds to the `use-pd-calibration` HTML attribute.
   */
  usePdCalibration?: boolean;
  /**
   * Reference PD in millimetres used when deriving `pdMm / referencePdMm`.
   * Defaults to the adult average calibration baseline used by the widget.
   *
   * Corresponds to the `reference-pd-mm` HTML attribute.
   */
  referencePdMm?: number;
  /**
   * Show the smart filter panel (audience / shape / style chips).
   * Defaults to true. Set to false to suppress the filter UI.
   *
   * Corresponds to the `enable-filters` HTML attribute on the custom element.
   */
  enableFilters?: boolean;
  /**
   * Show the search field above the filter chips.
   * Defaults to true. Set to false to suppress the search input.
   *
   * Corresponds to the `enable-search` HTML attribute on the custom element.
   */
  enableSearch?: boolean;
  /**
   * Show the frame size selector next to the variant strip.
   * Defaults to true. Set to false to suppress size pills.
   *
   * Corresponds to the `enable-size-selector` HTML attribute on the custom element.
   */
  enableSizeSelector?: boolean;
  /**
   * Show the "Similar Frames" carousel above the variant strip.
   * Defaults to true. Set to false to suppress the carousel.
   *
   * Corresponds to the `enable-similar-frames` HTML attribute on the custom element.
   */
  enableSimilarFrames?: boolean;
  /**
   * Widget display mode.
   * 'single' — standard single-product try-on (default).
   * 'compare' — side-by-side two-product comparison view.
   *
   * Corresponds to the `mode` HTML attribute on the custom element.
   */
  mode?: 'single' | 'compare';
}

/** Default values applied when attributes are not provided. */
export const DEFAULT_CONFIG: ForgeTryonConfig = {
  apiUrl: '',
  theme: 'auto',
  locale: 'en',
  enableFilters: true,
  enableSearch: true,
  enableSizeSelector: true,
  enableSimilarFrames: true,
  mode: 'single',
};

/**
 * Merges partial attribute overrides onto the default config.
 * Attribute values arrive as strings; this function coerces them.
 */
export function buildConfig(
  attrs: Partial<Record<string, string>>,
  overrides: Partial<ForgeTryonConfig> = {},
): ForgeTryonConfig {
  const rawTheme = attrs['theme'] ?? DEFAULT_CONFIG.theme;
  const theme: ForgeTryonConfig['theme'] = (['light', 'dark', 'auto'] as const).includes(
    rawTheme as ForgeTryonConfig['theme'],
  )
    ? (rawTheme as ForgeTryonConfig['theme'])
    : 'auto';

  const config: ForgeTryonConfig = {
    apiUrl: attrs['api-url'] ?? DEFAULT_CONFIG.apiUrl,
    theme,
    locale: attrs['locale'] ?? DEFAULT_CONFIG.locale,
    ...overrides,
  };

  const productId = attrs['product-id'];
  if (productId !== undefined) config.productId = productId;

  const storeId = attrs['store-id'];
  if (storeId !== undefined) config.storeId = storeId;

  const apiKey = attrs['api-key'];
  if (apiKey !== undefined) config.apiKey = apiKey;

  const analyticsId = attrs['analytics'];
  if (analyticsId !== undefined) config.analyticsId = analyticsId;

  const debug = attrs['debug'];
  if (debug !== undefined) config.debug = debug !== 'false';

  const cartUrl = attrs['cart-url'];
  if (cartUrl !== undefined) config.cartUrl = cartUrl;

  const cartPlatform = attrs['cart-platform'];
  if (cartPlatform !== undefined) {
    const validPlatforms: CartPlatform[] = ['nopcommerce', 'shopify', 'woocommerce', 'custom', 'none'];
    if ((validPlatforms as string[]).includes(cartPlatform)) {
      config.cartPlatform = cartPlatform as CartPlatform;
    }
  }

  const testVideoSrc = attrs['test-video-src'];
  if (testVideoSrc !== undefined) config.testVideoSrc = testVideoSrc;

  const rawIpdScaleFactor = attrs['ipd-scale-factor'];
  if (rawIpdScaleFactor !== undefined) {
    const ipdScaleFactor = Number(rawIpdScaleFactor);
    if (Number.isFinite(ipdScaleFactor) && ipdScaleFactor > 0) {
      config.ipdScaleFactor = ipdScaleFactor;
    }
  }

  const usePdCalibration = attrs['use-pd-calibration'];
  if (usePdCalibration !== undefined) config.usePdCalibration = usePdCalibration !== 'false';

  const rawReferencePdMm = attrs['reference-pd-mm'];
  if (rawReferencePdMm !== undefined) {
    const referencePdMm = Number(rawReferencePdMm);
    if (Number.isFinite(referencePdMm) && referencePdMm > 0) {
      config.referencePdMm = referencePdMm;
    }
  }

  // Sprint 2 filter/search/size-selector flags (default true when attribute absent)
  const enableFilters = attrs['enable-filters'];
  if (enableFilters !== undefined) config.enableFilters = enableFilters !== 'false';

  const enableSearch = attrs['enable-search'];
  if (enableSearch !== undefined) config.enableSearch = enableSearch !== 'false';

  const enableSizeSelector = attrs['enable-size-selector'];
  if (enableSizeSelector !== undefined) config.enableSizeSelector = enableSizeSelector !== 'false';

  // Sprint 3 — similar frames + compare mode flags (default true when attribute absent)
  const enableSimilarFrames = attrs['enable-similar-frames'];
  if (enableSimilarFrames !== undefined) config.enableSimilarFrames = enableSimilarFrames !== 'false';

  const rawMode = attrs['mode'];
  if (rawMode !== undefined) {
    if (rawMode === 'compare') {
      config.mode = 'compare';
    } else {
      config.mode = 'single'; // default for any unrecognised value
    }
  }

  return config;
}
